import {useBreadcrumbsController} from 'components/table-report/hooks/use-breadcrumbs-controller';
import {startFileLoading, stopFileLoading} from 'features/provide-custom-link';
import {uploadDataWithToken} from 'features/provide-custom-link/api/custom-link-handler.api';
import {getCustomHeaders} from 'features/provide-custom-link/lib/get-custom-headers';
import {ELocationStateKey, useAppHistory} from 'shared/hooks/use-app-history';
import {IHistoryLocationState} from 'shared/hooks/use-app-history/use-app-history-types';
import {useFileViewer} from 'shared/hooks/use-file-viewer';
import {TLoadFileArgs} from 'shared/hooks/use-file-viewer/use-file-viewer';
import {TStateSetter} from 'shared/types/generics';
import {TLinkDto, ELinkType} from 'shared/types/links';
import {showMessage} from 'shared/utils/notifications';
import {useAppDispatch} from 'store/config/hooks';

import {useResolveDynamicParameters} from './use-resolve-dynamic-parameters';

interface IUseCustomLinkHandlerArgs {
    link: TLinkDto;
    extraStateData?: Partial<IHistoryLocationState>;
    actionCode?: string;
    setLoading?: TStateSetter<boolean>;
}

/**
 * Хук для обработки действий CustomLink на основе типа link.urlType:
 * - MODAL | MODAL_WITH_TOKEN - открытие модального окна
 * - PDF_WITH_ERROR_FALLBACK | FILE_WITH_TOKEN | FILE - загрузка файла
 * - INTERNAL | INNER_TABLE_REPORT_DD - навигация по проекту с дополнительными данными extraStateData
 * - DATA_WITH_TOKEN - отправка данных во внешнюю систему
 *
 * @param {TLinkDto} link - Базовый URL
 * @param {Partial<IHistoryLocationState> | undefined} extraStateData - дополнительные данные для навигации
 * @param {string | undefined} actionCode - Код действия
 * @param {TStateSetter<boolean> | undefined} setLoading - сеттер для обновления стейта загрузки
 *
 * @returns {Function} handleLinkClick - обработчик нажатия по CustomLink
 * @returns {Function} checkIsLoading  - функция, возвращает статус загрузки файла
 * @returns {string} urlWithParams - url с динамическими параметрами
 *
 */
export const useCustomLinkHandler = ({
    link,
    extraStateData,
    actionCode,
    setLoading,
}: IUseCustomLinkHandlerArgs) => {
    const {pushWithStateUpdate} = useAppHistory();
    const dispatch = useAppDispatch();

    const {
        url = '/',
        urlType,
        filterParameters,
        queryParameters,
        bodyParameters,
        ignoreRequiredParameters,
        token,
        suppressMessages,
        method,
        bodyType,
        extraData,
        headerParameters,
    } = link ?? {};

    const {tableReportDrilldownParams} = extraData ?? {};

    const {
        name,
        docId,
        templateCode,
    } = tableReportDrilldownParams ?? {};

    const {urlWithParams, downloadFileUrl} = useResolveDynamicParameters({url, parameters: queryParameters});
    const {resolvedParameters: resolvedBodyParameters} = useResolveDynamicParameters({parameters: bodyParameters});

    const {loadFile, checkIsLoading} = useFileViewer({
        url: downloadFileUrl,
        urlType,
    });

    const loadFileWithRedux = (args?: TLoadFileArgs) => {
        loadFile({
            ...args,
            onStartLoading: () => dispatch(startFileLoading()),
            onCompleteLoading: () => dispatch(stopFileLoading()),
        });
    };

    const {getNextStepBreadcrumbs} = useBreadcrumbsController({
        docId,
        templateCode,
    });

    const linkMeta = {
        ignoreRequiredParameters,
    };

    const handleLinkClick = async (onModalOpen?: (link: TLinkDto) => void) => {
        if (checkIsLoading()) return;

        if (urlType === ELinkType.MODAL || urlType === ELinkType.MODAL_WITH_TOKEN) {
            onModalOpen?.(link);
            return;
        }

        if (urlType === ELinkType.FILE
            || urlType === ELinkType.FILE_WITH_TOKEN
            || urlType === ELinkType.PDF_WITH_ERROR_FALLBACK
        ) {
            const customHeaders = await getCustomHeaders(headerParameters) as [string, string][];

            // token приходит сразу готовым, поэтому оба сценария одинаковые.
            const headers = [] as [string, string][];
            if (token) {
                headers.push([
                    'Authorization', token,
                ]);
            }

            loadFileWithRedux({
                headers: [...headers, ...customHeaders],
            });
            return;
        }
        if (urlType === ELinkType.INTERNAL) {
            if (url) {
                pushWithStateUpdate(urlWithParams, {
                    ...(filterParameters ? {
                        [ELocationStateKey.LINK_FILTER_PARAMETERS]: filterParameters,
                    } : {}),
                    [ELocationStateKey.LINK_META]: linkMeta,
                    ...(extraStateData ?? {}),
                });
            }
        }
        if (urlType === ELinkType.INNER_TABLE_REPORT_DD) {
            if (!url || !name) {
                console.error(
                    `Отсутствуют необходимые параметры для осуществления перехода
                    \nсписок параметров: url=${url}, name=${name}`,
                );
                return;
            }

            const newBreadcrumbs = getNextStepBreadcrumbs({
                name,
                path: url,
            });

            if (!newBreadcrumbs) {
                console.error('Ошибка при генерации хлебных крошек для перехода');
                return;
            }

            pushWithStateUpdate(url, {
                [ELocationStateKey.TABLE_REPORT_BREADCRUMBS]: newBreadcrumbs,
                [ELocationStateKey.TABLE_REPORT_REQUEST_GENERAL_PARAMS]: filterParameters ?? {},
                // обнуляем параметры отчета, из которого уходим, чтобы они не передались при переходе
                [ELocationStateKey.TABLE_REPORT_LOCATION_DEFAULTS]: undefined,
            });
        }
        if (url && urlType === ELinkType.DATA_WITH_TOKEN) {
            const isUploadExternalSystemAction = actionCode === 'UPLOAD_EXTERNAL_SYSTEM';

            setLoading?.(true);

            if (isUploadExternalSystemAction) {
                showMessage({
                    message: 'Началось подключение к внешней системе',
                });
            }

            await uploadDataWithToken({
                method,
                urlWithParams,
                token,
                bodyType,
                suppressMessages,
                setLoading,
                resolvedBodyParameters,
            });
        }
    };

    return {
        handleLinkClick,
        checkIsLoading,
        urlWithParams,
    };
};
